<template>
  <div class="w-full h-full">
    <AlertDialog :open="showErrorModal" @update:open="(o) => !o && (showErrorModal = false)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Internet connection error</AlertDialogTitle>
          <AlertDialogDescription>It appears you're not connected to the internet. Please check your connection and try again.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction @click="showErrorModal = false">OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Drawer direction="right" :open="showFormModal" @update:open="(o) => !o && (showFormModal = false)">
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Create a webhook</DrawerTitle>
          <DrawerDescription>Name and select your webhook scope</DrawerDescription>
        </DrawerHeader>
        <div class="flex flex-col gap-4 overflow-y-auto px-4 flex-1">
          <div class="space-y-2">
            <label class="text-sm font-medium" for="create-webhook-request-url">Webhook url</label>
            <Input id="create-webhook-request-url" v-model="createWebhookRequest.url" type="text" />
          </div>
          <WebhookScopeSelect v-model="createWebhookRequest.scope" :options="scope" />
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="showFormModal = false">Cancel</Button>
          <Button :disabled="!validateForm" @click="createWebhook()">Create webhook</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
    <Card>
      <CardHeader>
        <CardTitle>Webhooks</CardTitle>
        <CardDescription>Manage your webhooks</CardDescription>
        <CardAction>
          <Button id="createContract" size="sm" @click="showFormModal = true">
            <Plus class="w-4 h-4" />
            Create webhook
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="apiKeys"
          :loading="loading"
          :error="error"
          @retry="getWebhooks()"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
        >
          <template #item-createdOn="apiKeys">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(apiKeys.createdOn) }}</span>
          </template>
          <template #item-active="apiKeys">
            <Badge v-if="apiKeys.active" variant="success">Active</Badge>
            <Badge v-else variant="warning">Deactivated</Badge>
          </template>
          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button variant="outline" size="sm" @click="$router.push('/developers/webhooks/' + retailers.id)">
                <Webhook class="w-4 h-4" />
                View webhook
              </Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>

  </div>
</template>

<script setup></script>

<script>
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
  Button, Badge, Input,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from '@boogi/ui'
import { Webhook, Plus } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'
import WebhookScopeSelect from '@/components/WebhookScopeSelect.vue'
import { WEBHOOK_SCOPES } from '@/constants/webhookScopes'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Input,
    WebhookScopeSelect,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    Webhook,
    Plus,
  },
  props: {},

  data() {
    return {
      scope: WEBHOOK_SCOPES,

      createWebhookRequest: {
        url: '',
        scope: [],
      },
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'URL', value: 'url' },
        { text: 'Created on', value: 'createdOn' },
        { text: 'Status', value: 'active' },
        { text: 'Action', value: 'action' },
      ],
      apiKeys: [],
    }
  },
  created() {
    this.getWebhooks()
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },
    async createWebhook() {
      var body = {
        url: this.createWebhookRequest.url,
        scope: this.createWebhookRequest.scope.map((a) => a.id),
      }

      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/webhook`, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          this.showFormModal = false
          var object = await response.json()
          this.apiKeys.push(object)
          this.loading = false
        }
      } catch (error) {
        return
      }
    },

    async getWebhooks() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/webhook`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.apiKeys = object
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    validateForm() {
      const urlPattern = new RegExp(
        '^(https:\\/\\/)?' + // protocol
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i' // fragment locator
      )

      return (
        this.createWebhookRequest.scope.length > 0 &&
        this.createWebhookRequest.url != '' &&
        urlPattern.test(this.createWebhookRequest.url)
      )
    },
  },
  mounted() {
    document.title = 'Dashboard | Webhooks'
  },
}
</script>
